import { Dispatch, SetStateAction } from 'react'
import {
  AtendimentosQueryResponse,
  AtendimentoView,
} from '../../../app/modules/apps/atendimento/core/_models'
import { SituacaoAso } from '../enums'
export type ID = undefined | null | number
export type PaginationState = {
  page: number
  size: number
  links?: Array<{ label: string; active: boolean; url: string | null; page: number | null }>
}
export type SortItem = {
  field: string
  order: 'asc' | 'desc'
}

export type SortState = {
  sort?: string
  order?: 'asc' | 'desc'
  fixedSort?: SortItem[]
}

export type QueryFilter = {
  field: string
  operator: '=' | '!=' | 'like' | 'ilike' | '~*' | '>' | '<' | '>=' | '<='
  value?: string | number | boolean
}

export type FilterState = {
  filter?: unknown
  filters?: QueryFilter[]
  filtersNPS?: unknown
}

export type TipoAtendimentoState = {
  tipo?: string
}
export type SearchState = {
  search?: string
}
export type Response<T> = {
  data: T | undefined
  page: number
  size: number
  totalElements: number
  totalPages: number
  elements: number
  empty: boolean
  first: boolean
  last: boolean
  payload?: {
    message?: string
    errors?: {
      [key: string]: Array<string>
    }
    pagination?: PaginationState
  }
}

export type TabsState = {
  tab?: 'atuais' | 'historico'
}

export type QueryState<TParams = undefined> = PaginationState &
  SortState &
  FilterState &
  SearchState &
  TabsState & {
    params?: TParams
    ready?: boolean
  }

export type QueryRequestContextProps<TParams = undefined> = {
  state: QueryState<TParams>
  updateState: (updates: Partial<QueryState<TParams>>) => void
}

export const initialQueryState: QueryState = {
  page: 0,
  size: 10,
}

export const initialQueryRequest = {
  state: initialQueryState,
  updateState: () => {},
}

export type QueryResponseContextProps<T> = {
  responseSemPaginacao?: T
  response?: Response<Array<T>>
  refetch: () => void
  isLoading: boolean
  /** Falha na última busca — usado pelo estado de erro do CustomTable. */
  isError?: boolean
  query: string
  atendimentosFinalizados?: AtendimentosQueryResponse | AtendimentoView[] | undefined
}
export type QueryResponseContextPropsSemPaginacao<T> = {
  response?: Array<T>
  singleResponse?: T
  refetch: () => void
  isLoading: boolean
  isError?: boolean
  query?: string
}
export const initialQueryResponseSemPaginacao = {
  refetch: () => {},
  isLoading: false,
  query: '',
}
export const initialQueryResponse = {
  refetch: () => {},
  isLoading: false,
  query: '',
}
export type ListViewContextProps = {
  isAllSelected?: boolean
  selected: Array<ID>
  onSelect: (selectedId: ID) => void
  clearSelected: () => void
  nomeFormulario?: string
  setNomeFormulario?: Dispatch<SetStateAction<string>>

  disabled?: boolean
  atendimentosFinalizados?: AtendimentosQueryResponse | AtendimentoView[] | undefined
  onSelectAll?: () => void
}
export const initialListView: ListViewContextProps = {
  selected: [],
  onSelect: () => {},
  clearSelected: () => {},
  disabled: false,
  isAllSelected: false,
  onSelectAll: () => {},
}

export type ISituacaoAso = {
  name?: SituacaoAso
  descricao?: string
  valor?: string
}

export type Enum<TName = string> = {
  descricao: string
  valor: string
  name: TName
  sigla?: string
  cadastro?: string
}

/**
 * Motivo de não realização de um atendimento (cancelamento ou falta), vindo do catálogo do
 * backend. O catálogo é a fonte única da taxonomia — o front não mantém lista própria.
 *
 * `name` é o identificador trafegado nas escritas (ex.: `FALTA_ENERGIA`); `valor` é o código
 * curto que o banco guarda. `sistema` e `ativo` chegam como string (`'true'`/`'false'`), no
 * mesmo formato dos demais enums servidos pelo backend: motivo de sistema não é ofertável em
 * tela e motivo inativo saiu do select, mas ambos continuam existindo para o histórico.
 */
export type MotivoNaoRealizacaoEnum = Enum & {
  classe: string
  classeDescricao: string
  sistema: string
  ativo: string
}

export type Erro = {
  id: number
  mensagem: string
}

export type VersionAPI = {
  softwareName: string
  vendorName: string
  version: string
  release: string
  build: string
  environment: string
  softwareDescription: string
}

export type ContatoList = {
  tipoContato?: string
  ativo?: boolean
  telefoneContato?: string
  telefoneMensagem?: string
  email?: string
  nome?: string
  id: number
}

export type EstadoView = {
  ibge?: number
  pais?: number
  sigla?: number
  descricao?: number
  id: number
}

export type NacionalidadeView = {
  descricao?: number
  id: number
  /**
   * Código do LEDI 8.4.2 (1 brasileira, 2 naturalizado, 3 estrangeiro), serializado como número pelo
   * backend (`NacionalidadeLedi.@JsonValue`). É **diferente** de `id`, que é a chave da linha do
   * catálogo — confundir os dois faz a tela exigir naturalização de quem é brasileiro.
   * Nulo em linha cuja descrição ficou fora da lista branca do backfill da F2c.
   */
  codigoLedi?: number | null
}

export type NacionalidadeList = {
  descricao?: number
  id: number
  /** Ver `NacionalidadeView.codigoLedi`: código do LEDI, não o id da linha. */
  codigoLedi?: number | null
}

/** Município do catálogo IBGE (F1 do epic e-SUS FCI). Somente leitura. */
export type MunicipioList = {
  id: number
  codigo: string
  nome: string
  estado?: { id: number; sigla?: string; descricao?: string }
}

/** País do catálogo do LEDI 8.4.2 (F2b). Somente leitura. */
export type PaisList = {
  id: number
  codigo: number
  nome: string
}

/** Etnia indígena do catálogo do LEDI 8.4.2 (F2b). Somente leitura. */
export type EtniaList = {
  id: number
  codigo: number
  nome: string
}

export type SuccessResponse = {
  message: string
}

export type PageResponseNacionalidadeList = Response<Array<NacionalidadeList>>

/** Página de municípios do catálogo IBGE — `GET /municipios/uf/{uf}` passou a ser paginado. */
export type PageResponseMunicipioList = Response<Array<MunicipioList>>

/** Página de países do catálogo do LEDI 8.4.2 — `GET /paises` passou a ser paginado. */
export type PageResponsePaisList = Response<Array<PaisList>>
